// src/pages/Login.js
import React, { useState, useContext } from 'react';
import { Navigate, useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { AuthContext } from '../context/AuthContext';
import Header from '../components/layout/Header';
import { FaEye, FaEyeSlash } from 'react-icons/fa';

function Login() {
  const { user, setUser, refreshUserProfile } = useContext(AuthContext);
  const navigate = useNavigate();
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');

  // If user is already logged in, redirect them based on role
  if (user) {
    if (user.role === 'admin') return <Navigate to="/admin" replace />;
    if (user.role === 'examiner') return <Navigate to="/examiner" replace />;
    if (user.role === 'archiver') return <Navigate to="/archiver" replace />;
    if (user.role === 'data_team') return <Navigate to="/data-team" replace />;
    if (user.role === 'security') return <Navigate to="/security" replace />;
    return <Navigate to="/" replace />;
  }

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    try {
      const res = await axios.post('/api/auth/login', {
        username,
        password
      });
      const { token, roles, id, forcePasswordChange } = res.data;
      if (token && roles && roles.length > 0) {
        localStorage.setItem('token', token);
        axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
        const role = roles[0];
        setUser({ username, role, id, forcePasswordChange: !!forcePasswordChange });
        // The login response itself has no fullName - fetch the full profile
        // now rather than leaving it missing until the next full page
        // reload (a fresh login is the one path that used to skip this).
        await refreshUserProfile(username);

        if (forcePasswordChange) navigate('/profile/edit');
        else if (role === 'admin') navigate('/admin');
        else if (role === 'examiner') navigate('/examiner');
        else if (role === 'archiver') navigate('/archiver');
        else if (role === 'data_team') navigate('/data-team');
        else if (role === 'security') navigate('/security');
        else navigate('/');
      } else {
        setError('Login failed. Please check your credentials.');
      }
    } catch (err) {
      setError('Login failed. Please check your credentials.');
    }
  };

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  const styles = {
    container: {
      display: 'flex',
      flexDirection: 'column',
      minHeight: '100vh'
    },
    content: {
      flex: 1,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '2rem'
    },
    card: {
      backgroundColor: '#fff',
      border: '1px solid #ccc',
      borderRadius: '8px',
      boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
      padding: '2rem',
      width: '100%',
      maxWidth: '400px',
      textAlign: 'center'
    },
    error: {
      color: 'red',
      marginBottom: '1rem'
    },
    form: {
      display: 'flex',
      flexDirection: 'column'
    },
    inputGroup: {
      position: 'relative',
      marginBottom: '1rem'
    },
    input: {
      padding: '0.75rem',
      border: '1px solid #ccc',
      borderRadius: '4px',
      fontSize: '1rem',
      outline: 'none',
      width: '100%'
    },
    passwordIcon: {
      position: 'absolute',
      top: '50%',
      right: '10px',
      transform: 'translateY(-50%)',
      cursor: 'pointer',
      color: '#666'
    },
    button: {
      padding: '0.75rem',
      backgroundColor: '#050f5e',
      color: '#fff',
      border: 'none',
      borderRadius: '4px',
      fontSize: '1rem',
      cursor: 'pointer',
      transition: 'background 0.3s ease'
    },
    forgotLink: {
      display: 'block',
      marginTop: '1rem',
      color: '#050f5e',
      textDecoration: 'none',
      fontWeight: 'bold'
    }
  };

  return (
    <div style={styles.container}>
      <Header />
      <div style={styles.content}>
        <div style={styles.card}>
          {error && <p style={styles.error}>{error}</p>}
          <form onSubmit={handleSubmit} style={styles.form} autoComplete="off">
            <input
              type="text"
              placeholder="Username"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              required
              style={styles.input}
              autoComplete="off"
              data-lpignore="true"
              data-1p-ignore="true"
              data-form-type="other"
            />
            <div style={styles.inputGroup}>
              <input
                type={showPassword ? 'text' : 'password'}
                placeholder="Password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                style={styles.input}
                autoComplete="new-password"
                data-lpignore="true"
                data-1p-ignore="true"
                data-form-type="other"
              />
              {showPassword ? (
                <FaEyeSlash style={styles.passwordIcon} onClick={togglePasswordVisibility} />
              ) : (
                <FaEye style={styles.passwordIcon} onClick={togglePasswordVisibility} />
              )}
            </div>
            <button type="submit" style={styles.button}>
              Login
            </button>
          </form>
          <Link to="/forgot-password" style={styles.forgotLink}>
            Forgot Password?
          </Link>
        </div>
      </div>
    </div>
  );
}

export default Login;
